/**
 * BRAND_CREDIBILITY_COLORS is a map of percentage ranges to their corresponding colors.
 * It is used to color the facts percentage badge in the brand sidebar and brand navigator.
 */
export const BRAND_CREDIBILITY_COLORS = {
  ['0-59']: 'rgb(41, 121, 255, 0.5)',
  ['60-69']: 'rgb(41, 121, 255, 0.6)',
  ['70-74']: 'rgb(41, 121, 255, 0.7)',
  ['75-79']: 'rgb(41, 121, 255, 0.8)',
  ['80-100']: 'rgb(41, 121, 255, 1)',
} as const;

/**
 * getBrandCredibilityColor returns the color for a given percentage.
 * It is used to color the brand credibility percentage badge in the brand sidebar and brand navigator.
 * @param percentage - The percentage to get the color for.
 * @returns The color for the given percentage.
 */
export const getBrandCredibilityColor = (percentage?: number) => {
  const percent = Math.round(percentage ?? 0);

  if (percent >= 80) {
    return BRAND_CREDIBILITY_COLORS['80-100'];
  } else if (percent >= 75) {
    return BRAND_CREDIBILITY_COLORS['75-79'];
  } else if (percent >= 70) {
    return BRAND_CREDIBILITY_COLORS['70-74'];
  } else if (percent >= 60) {
    return BRAND_CREDIBILITY_COLORS['60-69'];
  }
  return BRAND_CREDIBILITY_COLORS['0-59'];
};

/**
 * ARTICLE_FACTS_COLORS is a map of percentage ranges to their corresponding colors.
 * It is used to color the facts percentage badge in the brand sidebar and brand navigator.
 */
export const ARTICLE_FACTS_COLORS = {
  ['0-40']: { default: 'rgb(220, 38, 38)', reason: '#F5DADD' },
  ['41-60']: { default: 'rgb(255, 165, 0)', reason: '#F8DDAC' },
  ['61-75']: { default: 'rgb(91, 180, 91)', reason: '#D0F3D0' },
  ['75-100']: { default: 'rgb(0, 128, 0)', reason: '#D0F3D0' },
} as const;

/**
 * MISCOMPILATION GUARD — do not "simplify" these functions back into
 * if/else chains that RETURN `variant === 'default' ? X.default : X.reason`
 * ternaries. The Next 16 / Turbopack production minifier merges adjacent
 * else-if branches of that exact shape into one (`p>=41||p>=21 ? A : C`),
 * silently deleting a color range — shipped bundles rendered bias 21-40
 * as orange and facts 41-60 as light green (found 2026-07-09 via OG image
 * pixel-probing; staging/prod affected). Resolving a distinct range KEY
 * per branch and indexing once at the end compiles correctly.
 */
/**
 * getArticleFactsColor returns the color for a given percentage.
 * It is used to color the article facts percentage badge in the article card.
 * @param percentage - The percentage to get the color for.
 * @returns The color for the given percentage.
 */
export const getArticleFactsColor = (
  percentage?: number,
  variant: 'default' | 'reason' = 'default',
) => {
  const percent = Math.round(percentage ?? 0);
  let range: keyof typeof ARTICLE_FACTS_COLORS;
  if (percent >= 75) range = '75-100';
  else if (percent >= 61) range = '61-75';
  else if (percent >= 41) range = '41-60';
  else range = '0-40';
  return ARTICLE_FACTS_COLORS[range][variant];
};

/**
 * OPINIONS_FACTS_COLORS is a map of percentage ranges to their corresponding colors.
 * It is used to color the opinions percentage badge in the brand sidebar and brand navigator.
 */
export const OPINIONS_FACTS_COLORS = {
  ['0-40']: { default: 'rgb(0, 128, 0)', reason: '#D0F3D0' },
  ['41-60']: { default: 'rgb(91, 180, 91)', reason: '#D0F3D0' },
  ['61-75']: { default: 'rgb(255, 165, 0)', reason: '#F8DDAC' },
  ['75-100']: { default: 'rgb(220, 38, 38)', reason: '#F5DADD' },
} as const;

/**
 * getArticleOpinionsColor returns the color for a given percentage.
 * It is used to color the article opinions percentage badge in the article card.
 * @param percentage - The percentage to get the color for.
 * @returns The color for the given percentage.
 */
export const getArticleOpinionsColor = (
  percentage?: number,
  variant: 'default' | 'reason' = 'default',
) => {
  const percent = Math.round(percentage ?? 0);
  let range: keyof typeof OPINIONS_FACTS_COLORS;
  if (percent >= 75) range = '75-100';
  else if (percent >= 61) range = '61-75';
  else if (percent >= 41) range = '41-60';
  else range = '0-40';
  return OPINIONS_FACTS_COLORS[range][variant];
};

/**
 * ARTICLE_BIAS_COLORS is a map of percentage ranges to their corresponding colors.
 * It is used to color the bias percentage badge in the article card.
 */
export const ARTICLE_BIAS_COLORS = {
  ['0-20']: { default: 'rgb(0, 128, 0)', reason: '#D0F3D0' },
  ['21-40']: { default: 'rgb(91, 180, 91)', reason: '#D0F3D0' },
  ['41-65']: { default: 'rgb(255, 165, 0)', reason: '#F8DDAC' },
  ['66-100']: { default: 'rgb(220, 38, 38)', reason: '#F5DADD' },
} as const;

/**
 * getArticleBiasColor returns the color for a given percentage.
 * It is used to color the article bias percentage badge in the article card.
 * @param percentage - The percentage to get the color for.
 * @returns The color for the given percentage.
 */
export const getArticleBiasColor = (
  percentage?: number,
  variant: 'default' | 'reason' = 'default',
) => {
  const percent = Math.round(percentage ?? 0);
  let range: keyof typeof ARTICLE_BIAS_COLORS;
  if (percent >= 66) range = '66-100';
  else if (percent >= 41) range = '41-65';
  else if (percent >= 21) range = '21-40';
  else range = '0-20';
  return ARTICLE_BIAS_COLORS[range][variant];
};

/**
 * YT_CREDIBILITY_COLORS is a map of percentage ranges to their corresponding colors.
 * It is used to color the youtube video credibility percentage badge in the YT card.
 */
export const YT_CREDIBILITY_COLORS = {
  ['0-40']: { default: 'rgb(220, 38, 38)', reason: '#F5DADD' },
  ['41-50']: { default: 'rgb(255, 165, 0)', reason: '#F8DDAC' },
  ['51-70']: { default: 'rgb(91, 180, 91)', reason: '#D0F3D0' },
  ['71-100']: { default: 'rgb(0, 128, 0)', reason: '#D0F3D0' },
} as const;

/**
 * getYtCredibilityColor returns the color for a given percentage.
 * It is used to color the youtube video credibility percentage badge in the YT card.
 * @param percentage - The percentage to get the color for.
 * @returns The color for the given percentage.
 */
export const getYtCredibilityColor = (
  percentage?: number,
  variant: 'default' | 'reason' = 'default',
) => {
  const percent = Math.round(percentage ?? 0);
  let range: keyof typeof YT_CREDIBILITY_COLORS;
  if (percent >= 71) range = '71-100';
  else if (percent >= 51) range = '51-70';
  else if (percent >= 41) range = '41-50';
  else range = '0-40';
  return YT_CREDIBILITY_COLORS[range][variant];
};

/**
 * YT_BIAS_COLORS is a map of percentage ranges to their corresponding colors.
 * It is used to color the youtube video bias percentage badge in the YT card.
 */
export const YT_BIAS_COLORS = {
  ['0-10']: { default: 'rgb(0, 128, 0)', reason: '#D0F3D0' },
  ['11-20']: { default: 'rgb(91, 180, 91)', reason: '#D0F3D0' },
  ['21-50']: { default: 'rgb(255, 165, 0)', reason: '#F8DDAC' },
  ['51-100']: { default: 'rgb(220, 38, 38)', reason: '#F5DADD' },
} as const;

/**
 * getYtBiasColor returns the color for a given percentage.
 * It is used to color the youtube video bias percentage badge in the YT card.
 * @param percentage - The percentage to get the color for.
 * @returns The color for the given percentage.
 */
export const getYtBiasColor = (
  percentage?: number,
  variant: 'default' | 'reason' = 'default',
) => {
  const percent = Math.round(percentage ?? 0);
  let range: keyof typeof YT_BIAS_COLORS;
  if (percent >= 51) range = '51-100';
  else if (percent >= 21) range = '21-50';
  else if (percent >= 11) range = '11-20';
  else range = '0-10';
  return YT_BIAS_COLORS[range][variant];
};

/**
 * YT_ORIGINALITY_COLORS is a map of percentage ranges to their corresponding colors.
 * It is used to color the youtube video originality percentage badge in the YT card.
 */
export const YT_ORIGINALITY_COLORS = {
  ['0-40']: { default: '#dc2626', reason: '#F5DADD' },
  ['41-50']: { default: '#ffa500', reason: '#F8DDAC' },
  ['51-70']: { default: '#5bb45b', reason: '#D0F3D0' },
  ['71-100']: { default: '#008000', reason: '#D0F3D0' },
} as const;

/**
 * getYtOriginalityColor returns the color for a given percentage.
 * It is used to color the youtube video originality percentage badge in the YT card.
 * @param percentage - The percentage to get the color for.
 * @returns The color for the given percentage.
 */
export const getYtOriginalityColor = (
  percentage?: number,
  variant: 'default' | 'reason' = 'default',
) => {
  const percent = Math.round(percentage ?? 0);
  let range: keyof typeof YT_ORIGINALITY_COLORS;
  if (percent >= 71) range = '71-100';
  else if (percent >= 51) range = '51-70';
  else if (percent >= 41) range = '41-50';
  else range = '0-40';
  return YT_ORIGINALITY_COLORS[range][variant];
};

/**
 * YT_DEPTH_COLORS is a map of percentage ranges to their corresponding colors.
 * It is used to color the youtube video depth percentage badge in the YT card.
 */
export const YT_DEPTH_COLORS = {
  ['0-40']: { default: 'rgb(220, 38, 38)', reason: '#F5DADD' },
  ['41-50']: { default: 'rgb(255, 165, 0)', reason: '#F8DDAC' },
  ['51-70']: { default: 'rgb(91, 180, 91)', reason: '#D0F3D0' },
  ['71-100']: { default: 'rgb(0, 128, 0)', reason: '#D0F3D0' },
} as const;

/**
 * getYtDepthColor returns the color for a given percentage.
 * It is used to color the youtube video depth percentage badge in the YT card.
 * @param percentage - The percentage to get the color for.
 * @returns The color for the given percentage.
 */
export const getYtDepthColor = (
  percentage?: number,
  variant: 'default' | 'reason' = 'default',
) => {
  const percent = Math.round(percentage ?? 0);
  let range: keyof typeof YT_DEPTH_COLORS;
  if (percent >= 71) range = '71-100';
  else if (percent >= 51) range = '51-70';
  else if (percent >= 41) range = '41-50';
  else range = '0-40';
  return YT_DEPTH_COLORS[range][variant];
};
